import type { PlatformError } from "./client";

/* One place that turns a failure into something a player can read.

   Rules: name what did not happen, say whether waiting helps, and never
   apologise or say "something went wrong". The raw API message is deliberately
   not shown — it is written for developers and sometimes leaks internals. */

type Subject =
  | "games"
  | "bets"
  | "promotions"
  | "leaderboard"
  | "wallet"
  | "account"
  | "rewards"
  | "sportsbook";

const NOUN: Record<Subject, string> = {
  games: "the game catalogue",
  bets: "the live feed",
  promotions: "promotions",
  leaderboard: "the leaderboard",
  wallet: "your wallet",
  account: "your account",
  rewards: "your rewards",
  sportsbook: "the sportsbook",
};

const REASON: Record<string, string> = {
  ZERO_AMOUNT: "Enter an amount greater than zero.",
  BELOW_MINIMUM_AMOUNT: "That is below the minimum withdrawal for this network.",
  INSUFFICIENT_BALANCE: "That is more than your balance.",
  INVALID_ADDRESS: "That is not a valid address for this network.",
  REQUIRED_WAGERING_NOT_MET: "There is wagering left on a bonus. Finish it before withdrawing.",
  WITHDRAWAL_LIMIT_REACHED:
    "That is over the withdrawal limit on this account. Try a smaller amount, or come back later.",
  EMAIL_VERIFICATION_REQUIRED:
    "Withdrawals need a verified email address. Verify it in account settings, then try again.",
  CONTACT_SUPPORT: "This account cannot withdraw right now. Contact support — waiting will not change it.",
  /* The platform is still settling the wagering progress of recent bets. It
     clears on its own within moments, so the honest advice is to wait. */
  WAGERING_PROGRESSION_PENDING:
    "Your wagering progress is still being processed. Try again in a moment.",
  "Nickname can only be changed once per account":
    "Your nickname has already been changed once. It cannot be changed again.",
  NO_CLAIMABLE_REWARDS: "There is nothing left to claim right now. Pressing again will not change that.",
};

export const messageFor = (
  error: PlatformError,
  subject: Subject,
  options: { operation?: "read" | "write" } = {},
) => {
  const noun = NOUN[subject];
  const reason = REASON[error.message];
  const isWrite = options.operation === "write";

  if (reason) {
    return reason;
  }

  switch (error.kind) {
    case "timeout":
      return `${capitalise(noun)} took too long to answer. It is usually back within a minute.`;

    case "network":
      return `We could not reach the service behind ${noun}. Check your connection, then try again.`;

    case "upstream":
      return `The service behind ${noun} is having trouble. Nothing is wrong on your side.`;

    case "rate-limited":
      return "Too many requests in a short window. Give it a few seconds.";

    case "unauthorized":
      return isWrite ? `Sign in to make changes to ${noun}.` : `Sign in to see ${noun}.`;

    case "forbidden":
      return `${capitalise(noun)} is not available from your location.`;

    /* Phrased so it works for a plural noun too — "Promotions no longer
       exists." was already wrong, and a claim that 404s because the reward is
       already spent must not tell a player their account is gone. */
    case "not-found":
      return isWrite
        ? `${capitalise(noun)} could not be found, so nothing changed.`
        : `${capitalise(noun)} could not be found.`;

    case "invalid":
      return isWrite
        ? `${capitalise(noun)} did not update — the request was rejected. This one is on us.`
        : `${capitalise(noun)} could not be loaded — the request was rejected. This one is on us.`;

    case "conflict":
      return "That is already in use. Try a different one.";

    default:
      return isWrite
        ? `${capitalise(noun)} could not be updated right now.`
        : `${capitalise(noun)} is unavailable right now.`;
  }
};

const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/* ------------------------------------------------------------- withdrawals

   The whitelabel's getWithdrawErrorMessage (react-sdk-v2), string for string,
   so a player reads the same refusal on either front end. It returns a list
   because a wagering refusal can name two separate requirements — AML and an
   active bonus — and each is its own line.

   Every figure in `details` is in the bankroll token's base units, the same
   scale as the balance the withdrawal is taken from, so `token` is the
   bankroll's token and not the route's destination token. KYC_REQUIRED is
   mapped for completeness, but callers open the verification prompt for it
   instead of showing a line (useHandleKycRequired). */

export type WithdrawErrorToken = { decimals: number; symbol: string };

type WithdrawErrorDetails = {
  remainingWagerAmount?: unknown;
  amlRemainingWagerAmount?: unknown;
  trancheRemainingWagerAmount?: unknown;
  remainingDailyWithdrawalLimit?: unknown;
};

const WITHDRAWAL_FAILED = "Withdrawal failed!";
const REGION = "Withdrawals are not available in your region.";
const LIMIT_REACHED = "You have reached the withdrawal limit within the given time period.";

/* A base-unit integer string from `details`, or null for anything else — a
   malformed figure costs the figure, never the message. */
const baseUnits = (value: unknown): bigint | null =>
  typeof value === "string" && /^\d+$/.test(value) ? BigInt(value) : null;

/* viem's formatUnits as the SDK's formatTokenBalance uses it: thousands
   grouped, trailing zeros dropped, at most two decimals, truncated. */
export const formatTokenBalance = (value: bigint, decimals: number) => {
  const divisor = 10n ** BigInt(decimals);
  const whole = (value / divisor).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = decimals > 0 ? (value % divisor).toString().padStart(decimals, "0").replace(/0+$/, "") : "";

  return fraction ? `${whole}.${fraction.slice(0, 2)}` : whole;
};

/* A wager still owed is rounded up to the cent before it is shown: the
   truncated figure would be a little short, and wagering exactly what we
   printed must clear the requirement. */
const formatWagerAmount = (amount: bigint, decimals: number) => {
  if (decimals <= 2) {
    return formatTokenBalance(amount, decimals);
  }

  const precision = 10n ** BigInt(decimals - 2);

  return formatTokenBalance(((amount + precision - 1n) / precision) * precision, decimals);
};

export const withdrawErrorMessages = (error: PlatformError, token?: WithdrawErrorToken): string[] => {
  const details = (typeof error.details === "object" && error.details !== null
    ? error.details
    : {}) as WithdrawErrorDetails;

  switch (error.message) {
    case "GEOBLOCKED":
      return [REGION];
    case "CONTACT_SUPPORT":
      return ["Please contact support."];
    case "EMAIL_VERIFICATION_REQUIRED":
      return ["Please verify your email address before withdrawing."];
    case "INVALID_ADDRESS":
      return ["Please enter a valid wallet address."];
    case "WITHDRAWAL_LIMIT_REACHED": {
      const remaining = baseUnits(details.remainingDailyWithdrawalLimit);

      /* Less than one whole token left is not worth offering. */
      if (remaining === null || !token || remaining < 10n ** BigInt(token.decimals)) {
        return [LIMIT_REACHED];
      }

      return [
        `Daily withdrawal limit reached. You can still withdraw up to ${formatTokenBalance(
          remaining,
          token.decimals,
        )} ${token.symbol}.`,
      ];
    }
    case "REQUIRED_WAGERING_NOT_MET": {
      if (!token) {
        return [WITHDRAWAL_FAILED];
      }

      const messages: string[] = [];
      const aml = baseUnits(details.amlRemainingWagerAmount);
      const tranche = baseUnits(details.trancheRemainingWagerAmount);

      if (aml !== null && aml > 0n) {
        messages.push(
          `To withdraw, please wager additional ${formatWagerAmount(aml, token.decimals)} ${token.symbol} to meet the AML requirements.`,
        );
      }

      if (tranche !== null && tranche > 0n) {
        messages.push(
          `To withdraw, please wager additional ${formatWagerAmount(tranche, token.decimals)} ${token.symbol} to meet the active bonus requirements.`,
        );
      }

      if (messages.length > 0) {
        return messages;
      }

      const remaining = baseUnits(details.remainingWagerAmount);

      if (remaining === null) {
        return [WITHDRAWAL_FAILED];
      }

      return [
        `To withdraw, please wager an additional ${formatWagerAmount(remaining, token.decimals)} ${token.symbol} to meet wagering requirements.`,
      ];
    }
    /* Not in the whitelabel's map, but the platform sends it: the bets that
       would clear the requirement are still being settled. */
    case "WAGERING_PROGRESSION_PENDING":
      return [REASON.WAGERING_PROGRESSION_PENDING];
    case "KYC_REQUIRED":
      return ["Identity verification is required before you can withdraw."];
    case "INSUFFICIENT_BALANCE":
      return ["You have insufficient balance."];
    default:
      if (error.status === 403) {
        return [REGION];
      }

      if (error.status === 400) {
        return ["Invalid request. Please check your details."];
      }

      return [WITHDRAWAL_FAILED];
  }
};

/* The reason on a `stablemint:withdrawal:error` notification, which arrives
   over the realtime stream after a bank withdrawal was handed to Stablemint —
   the whitelabel's getStablemintWithdrawErrorMessage. */
export const stablemintWithdrawErrorMessage = (reason: string) => {
  switch (reason) {
    case "ZERO_AMOUNT":
      return "Withdrawal amount cannot be zero.";
    case "BELOW_MINIMUM_AMOUNT":
      return "Amount is below the minimum withdrawal amount.";
    case "WITHDRAWAL_LIMIT_REACHED":
      return LIMIT_REACHED;
    case "CONTACT_SUPPORT":
      return "Please contact support.";
    case "EMAIL_VERIFICATION_REQUIRED":
      return "Please verify your email address before withdrawing.";
    case "INVALID_ADDRESS":
      return "Please enter a valid wallet address.";
    case "REQUIRED_WAGERING_NOT_MET":
      return "Wagering requirement not met, please contact support.";
    case "KYC_REQUIRED":
      return "Identity verification is required before you can withdraw.";
    case "INSUFFICIENT_BALANCE":
      return "You have insufficient balance.";
    case "BALANCE_UPDATE_FAILED":
      return "Withdrawal failed, please try again.";
    default:
      return WITHDRAWAL_FAILED;
  }
};

/* --------------------------------------------------------------- fiat rails

   A Stablemint session that would not open, as the whitelabel's
   CardDepositContent / BankTransferContent word it. A 404 is the route having
   gone since the bankrolls were read — the caller reads them again. A refused
   fiat withdrawal reads like any other withdrawal (withdrawErrorMessages),
   apart from the same 404. */

export const FIAT_METHOD_GONE = "This payment method is no longer available. Please choose a different method.";

export const fiatDepositErrorMessage = (error: PlatformError, method: "card" | "bank") => {
  if (error.status === 403) {
    return method === "card"
      ? "Card deposits are not available in your region."
      : "Bank deposits are not available in your region.";
  }

  if (error.status === 404) {
    return FIAT_METHOD_GONE;
  }

  if (error.status === 400) {
    return "Invalid request. Please check your details.";
  }

  return method === "card"
    ? "Failed to initiate card deposit. Please try again."
    : "Failed to initiate bank deposit. Please try again.";
};
